import {memo, useEffect} from 'react';

import styled from 'styled-components';

import {getLocalizationService} from '~/shared/services/localisationService';
import {media} from '~/shared/theme/media';
import {bodyRegular, headingXS, smallRegular} from '~/shared/theme/typography';
import TenbisCard from '~/shared/components/TenbisCard';
import {flipOnLTR, rotateOnRTL} from '~/shared/theme/utils';
import {MoneyCard, RevealMoneyCardResponse} from '~/shared/services/bffService/api/interfaces/responses';
import Button from '~/shared/components/Button';
import {MoneycardOtlRuleManagement, User} from '~/shared/store/models';
import {NewTag} from '~/personalArea/components/Sections/MyTenbis/styled';
import {FlexColumn} from '~/shared/theme/FlexLayout';
import {LoaderContainer} from '~/personalArea/components/Sections/Shared';
import {SimpleSpinnerLoader} from '~/shared/components/Loaders';
import {createLogger} from '~/shared/logging';

import useMyTenbisActions from './useMyTenbisActions';
import ActionsSection from './ActionsSection';
import ChevronLeft from './assets/chevronLeft.svg?react';
import OtlRulesFetchError from './OtlRulesFetchError';

const logger = createLogger('TenbisCardView');

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${media.minLargeMobile`
    gap: 32px;
  `}
  ${media.minLargeTablet`
    gap: 24px;
  `}
`;

const Content = styled.div<{isActive?: boolean}>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  ${({isActive}) => media.minLargeTablet`
    flex-direction: ${isActive ? 'row-reverse' : 'row'};
    justify-content: space-between;
    gap: auto;
  `}
`;

const Header = styled(FlexColumn)`
  gap: 8px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Title = styled.h2`
  ${headingXS}
  color: ${({theme}) => theme.colors.contentDefault};
`;

const Disclaimer = styled.span`
  ${bodyRegular}
  display: block;
`;

const BackButtonContainer = styled.div`
  ${flipOnLTR`
    padding-left: 16px;
    border-left: 1px solid ${({theme}) => theme.colors.borderStrong};
  `}
`;

const BackButton = styled(Button)`
  display: flex;
  gap: 4px;
`;

const BackButtonArrow = styled(ChevronLeft)`
  ${rotateOnRTL}
`;

const BackButtonText = styled.span`
  ${smallRegular}
  display: block;
`;

const StyledTenbisCard = styled(TenbisCard)`
  ${media.minLargeMobile`
    align-self: center;
  `}
  ${media.minLargeTablet`
    align-self: unset;
  `}
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const getDefaultCardNumber = (cardLastDigits: string) => `XXXX-XXXX-XXXX-${cardLastDigits}`;

const getFormattedCardNumber = (mcFullNumber: string) => {
  const chunkedMcFullNumber = mcFullNumber.match(/\d{4}/g);
  if (!chunkedMcFullNumber) {
    logger.warn('MoneyCard number is malformed');
    return '';
  }
  return chunkedMcFullNumber.join('-');
};

type TenbisCardViewProps = {
  currentMoneycard: MoneyCard;
  cardDetails?: RevealMoneyCardResponse;
  otlData: MoneycardOtlRuleManagement | null;
  fetchOtl: () => Promise<MoneycardOtlRuleManagement | null>;
  refetchPayments: () => Promise<void>;
  onBackButtonClick?: (() => void) | null;
  onActivateMoneycardClick?: (id: string) => void;
  userData: User | null;
  onShowDetailsClick?: () => void;
  onHideDetailsClick?: () => void;
  lastOtlCardId?: string;
  showErrorScreen?: boolean;
};

const TenbisCardView = ({
  currentMoneycard,
  cardDetails,
  otlData,
  fetchOtl,
  refetchPayments,
  onBackButtonClick,
  onActivateMoneycardClick,
  userData,
  onShowDetailsClick,
  onHideDetailsClick,
  lastOtlCardId,
  showErrorScreen,
}: TenbisCardViewProps) => {
  const {t} = getLocalizationService();

  const actions = useMyTenbisActions({
    currentMoneycard,
    otlData,
    fetchOtl,
    refetchPayments,
  });

  const activateCardHandler = () => {
    if (onActivateMoneycardClick) {
      onActivateMoneycardClick(currentMoneycard.id);
    }
  };

  useEffect(() => {
    const cardId = currentMoneycard.id;
    if (!!cardId && lastOtlCardId !== cardId) {
      fetchOtl();
    }
  }, [currentMoneycard, lastOtlCardId, fetchOtl]);

  if (showErrorScreen) {
    return (
      <Root>
        <OtlRulesFetchError onRetryClick={fetchOtl} />
      </Root>
    );
  }

  if (!otlData) {
    return (
      <LoaderContainer>
        <SimpleSpinnerLoader variant="pieBig" />
      </LoaderContainer>
    );
  }

  const cardFullNumber = cardDetails ?
    getFormattedCardNumber(cardDetails.mcFullNumber) :
    getDefaultCardNumber(currentMoneycard.cardLastDigits);

  return (
    <Root>
      <Header>
        <TitleContainer>
          {onBackButtonClick && (
            <BackButtonContainer>
              <BackButton onClick={onBackButtonClick}>
                <BackButtonArrow />
                <BackButtonText>
                  {t('back_2')}
                </BackButtonText>
              </BackButton>
            </BackButtonContainer>
          )}
          <TitleWrapper>
            <Title>{t('tenbis_card_xxx', {fourDigits: currentMoneycard.cardLastDigits})}</Title>
            {!currentMoneycard.isActivated && <NewTag>{t('new_card')}</NewTag>}
          </TitleWrapper>
        </TitleContainer>
        {!currentMoneycard.isActivated && <Disclaimer>{t('the_card_has_not_been_activated_yet_it_must_be_activated_to_start_using_it')}</Disclaimer>}
      </Header>
      <Content isActive={currentMoneycard.isActivated}>
        <StyledTenbisCard
          isActive={currentMoneycard.isActivated}
          isRevealed={!!cardDetails}
          cardHolderName={`${userData?.firstName} ${userData?.lastName}`}
          cardNumber={cardFullNumber}
          cvv={cardDetails?.cVV}
          expirationMonth={cardDetails?.expirationMonth}
          expirationYear={cardDetails?.expirationYear}
          hasTimer={!!cardDetails?.cVV}
          onActivateCardClick={activateCardHandler}
          onShowDetailsClick={onShowDetailsClick}
          onHideDetailsClick={onHideDetailsClick}
        />
        {currentMoneycard.isActivated && <ActionsSection actions={actions} />}
      </Content>
    </Root>
  );
};

export default memo(TenbisCardView);
